const WCFJIPFeedback = (() => {
  const recentLogs = [];
  const maxLogs = 40;
  let screenshotToolPromise;

  const toText = (value) => {
    if (value instanceof Error) {
      return value.stack || value.message;
    }

    if (typeof value === 'string') {
      return value;
    }

    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  };

  const rememberLog = (type, values) => {
    recentLogs.push({
      type,
      message: Array.from(values).map(toText).join(' '),
      at: new Date().toISOString(),
    });

    if (recentLogs.length > maxLogs) {
      recentLogs.shift();
    }
  };

  ['error', 'warn'].forEach((type) => {
    const original = window.console && window.console[type];

    if (!original) {
      return;
    }

    window.console[type] = (...args) => {
      rememberLog(type, args);
      original.apply(window.console, args);
    };
  });

  window.addEventListener('error', (event) => {
    rememberLog('error', [
      event.message,
      event.filename ? `${event.filename}:${event.lineno || 0}:${event.colno || 0}` : '',
    ]);
  });

  window.addEventListener('unhandledrejection', (event) => {
    rememberLog('unhandledrejection', [event.reason || 'Unhandled promise rejection']);
  });

  const getConnectionDetails = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (!connection) {
      return null;
    }

    return {
      effectiveType: connection.effectiveType || '',
      downlink: connection.downlink || '',
      rtt: connection.rtt || '',
      saveData: Boolean(connection.saveData),
    };
  };

  const getDeviceDetails = () => ({
    url: window.location.href,
    title: document.title,
    referrer: document.referrer,
    language: navigator.language,
    languages: navigator.languages,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    online: navigator.onLine,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    },
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
    },
    pixelRatio: window.devicePixelRatio || 1,
    deviceMemory: navigator.deviceMemory || '',
    hardwareConcurrency: navigator.hardwareConcurrency || '',
    connection: getConnectionDetails(),
  });

  const shrinkCanvas = (canvas) => {
    const maxWidth = 1400;

    if (canvas.width <= maxWidth) {
      return canvas;
    }

    const ratio = maxWidth / canvas.width;
    const scaled = document.createElement('canvas');
    scaled.width = maxWidth;
    scaled.height = Math.round(canvas.height * ratio);
    scaled.getContext('2d').drawImage(canvas, 0, 0, scaled.width, scaled.height);

    return scaled;
  };

  const loadScreenshotTool = (config) => {
    if (window.html2canvas) {
      return Promise.resolve(window.html2canvas);
    }

    if (screenshotToolPromise) {
      return screenshotToolPromise;
    }

    screenshotToolPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = config.screenshotUrl;
      script.async = true;
      script.onload = () => {
        if (window.html2canvas) {
          resolve(window.html2canvas);
        } else {
          reject(new Error('Screenshot tool did not load.'));
        }
      };
      script.onerror = () => reject(new Error('Screenshot tool could not be loaded.'));
      document.head.appendChild(script);
    });

    return screenshotToolPromise;
  };

  const captureScreenshot = async (widget, config) => {
    const html2canvas = await loadScreenshotTool(config);
    widget.classList.add('is-capturing');

    try {
      const canvas = await html2canvas(document.body, {
        backgroundColor: null,
        height: window.innerHeight,
        logging: false,
        scale: Math.min(window.devicePixelRatio || 1, 1.5),
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        useCORS: true,
        width: window.innerWidth,
        windowHeight: window.innerHeight,
        windowWidth: window.innerWidth,
      });

      return shrinkCanvas(canvas).toDataURL('image/jpeg', 0.72);
    } finally {
      widget.classList.remove('is-capturing');
    }
  };

  const setStatus = (status, message, isError = false) => {
    status.textContent = message;
    status.classList.toggle('is-error', isError);
  };

  const init = () => {
    const config = window.WCFJIPFeedbackConfig;
    const widget = document.querySelector('[data-feedback-widget]');

    if (!config || !widget) {
      return;
    }

    const openButton = widget.querySelector('[data-feedback-open]');
    const closeButton = widget.querySelector('[data-feedback-close]');
    const panel = widget.querySelector('[data-feedback-panel]');
    const form = widget.querySelector('[data-feedback-form]');
    const submitButton = widget.querySelector('[data-feedback-submit]');
    const status = widget.querySelector('[data-feedback-status]');

    if (!openButton || !closeButton || !panel || !form || !submitButton || !status) {
      return;
    }

    const openPanel = () => {
      panel.hidden = false;
      widget.classList.add('is-open');
      openButton.setAttribute('aria-expanded', 'true');
      const firstField = form.querySelector('textarea, input, button');

      if (firstField) {
        firstField.focus();
      }
    };

    const closePanel = () => {
      panel.hidden = true;
      widget.classList.remove('is-open');
      openButton.setAttribute('aria-expanded', 'false');

      if (openButton.offsetParent) {
        openButton.focus();
        return;
      }

      const navToggle = document.querySelector('[data-nav-toggle]');

      if (navToggle) {
        navToggle.focus();
      }
    };

    openButton.addEventListener('click', openPanel);
    closeButton.addEventListener('click', closePanel);

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-feedback-trigger]');

      if (!trigger) {
        return;
      }

      event.preventDefault();
      openPanel();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) {
        closePanel();
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const formData = new FormData(form);
      const payload = new FormData();

      submitButton.disabled = true;
      setStatus(status, config.sendingMessage);

      payload.append('action', 'wcfjip_feedback_submit');
      payload.append('nonce', config.nonce);
      payload.append('message', formData.get('message') || '');
      payload.append('email', formData.get('email') || '');
      payload.append('website', formData.get('website') || '');
      payload.append('page_url', window.location.href);
      payload.append('page_title', document.title);

      if (formData.get('include_details')) {
        payload.append('details', JSON.stringify(getDeviceDetails()));
        payload.append('logs', JSON.stringify(recentLogs));
      }

      if (formData.get('include_screenshot')) {
        try {
          setStatus(status, config.screenshotText);
          payload.append('screenshot', await captureScreenshot(widget, config));
        } catch (error) {
          rememberLog('screenshot', [error]);
          setStatus(status, config.screenshotError);
        }
      }

      try {
        setStatus(status, config.sendingMessage);

        const response = await window.fetch(config.ajaxUrl, {
          method: 'POST',
          body: payload,
          credentials: 'same-origin',
        });
        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error((result.data && result.data.message) || config.errorMessage);
        }

        form.reset();
        form.querySelector('[name="include_screenshot"]').checked = true;
        setStatus(status, result.data.message || config.successMessage);
      } catch (error) {
        rememberLog('send', [error]);
        setStatus(status, error.message || config.errorMessage, true);
      } finally {
        submitButton.disabled = false;
      }
    });
  };

  return { init };
})();

document.addEventListener('DOMContentLoaded', () => WCFJIPFeedback.init());
